import React, { useState, useEffect, ReactNode } from 'react';
import { Button } from "@/ui-components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@/ui-components/ui/dialog";
import { Calendar as CalendarIcon, Clock, CalendarClock } from "lucide-react";
import { Calendar } from "@/ui-components/ui/calendar";
import { StandardDialogHeader } from "@/util-components/dialog/components/StandardDialogHeader";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Input } from "@/ui-components/ui/input";

interface SchedulePostDialogProps {
  /**
   * The trigger element that opens the dialog (e.g., Button, Icon, etc.)
   */
  trigger?: ReactNode;
  /**
   * Callback function called after successfully picking a date and time
   */
  onSave: (date: Date) => void;
  /**
   * Initial selected date, if any
   */
  initialDate?: Date | null;
}

/**
 * Dialog component for scheduling posts
 * Allows users to pick a date and time for the post to be published
 */
export const SchedulePostDialog: React.FC<SchedulePostDialogProps> = ({
  trigger,
  onSave,
  initialDate,
}) => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(initialDate || undefined);
  const [time, setTime] = useState<string>(
    initialDate ? format(initialDate, "HH:mm") : "12:00"
  );
  const [error, setError] = useState<string | null>(null);

  // Reset internal state when dialog opens
  useEffect(() => {
    if (open) {
      if (initialDate) {
        setDate(initialDate);
        setTime(format(initialDate, "HH:mm"));
      } else {
        setDate(undefined);
        setTime("12:00");
      }
      setError(null);
    }
  }, [open, initialDate]);

  /**
   * Handle form submission
   */
  const handleSave = () => {
    if (!date) {
      setError("Bitte wähle ein Datum aus.");
      return;
    }

    // Combine date and time
    const [hours, minutes] = time.split(":").map(Number);
    const scheduledDate = new Date(date);
    scheduledDate.setHours(hours, minutes, 0, 0);

    // Validate that the scheduled date is in the future
    if (scheduledDate <= new Date()) {
      setError("Das geplante Datum muss in der Zukunft liegen.");
      return;
    }

    setError(null);
    onSave(scheduledDate);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-md p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0">
        <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
          <StandardDialogHeader
            icon={CalendarClock}
            title="Post einplanen"
            description="Wähle das Datum und die Uhrzeit, wann dieser Post veröffentlicht werden soll."
            className="p-0 border-none"
          />
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-5 sm:space-y-6">
            {/* Calendar Picker */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Datum</span>
              </div>
              <div className="border bg-background rounded-xl overflow-hidden shadow-sm inline-flex justify-center p-2">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(newDate) => {
                    setDate(newDate);
                    setError(null);
                  }}
                  disabled={(d) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return d < today;
                  }}
                  className="rounded-md"
                />
              </div>
            </div>

            {/* Time Picker */}
            <div className="flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Uhrzeit</span>
              </div>
              
              <Input
                type="time"
                value={time}
                onChange={(e) => {
                  setTime(e.target.value);
                  setError(null);
                }}
                className="h-11 w-full text-center text-lg font-medium"
              />
            </div>

            {error && (
              <p className="text-sm font-medium text-destructive text-center animate-in fade-in duration-200">
                {error}
              </p>
            )}

            {date && !error && (
              <div className="bg-muted/30 text-foreground p-3 rounded-xl text-sm text-center border animate-in fade-in duration-200">
                Der Post wird am <strong>{format(date, "d. MMMM yyyy", { locale: de })}</strong> um <strong>{time} Uhr</strong> eingeplant.
              </div>
            )}
          </div>

          <DialogFooter className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-gradient-to-t from-background via-background to-background/95 !flex-col gap-3 sm:!flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="flex w-full sm:w-auto">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900 sm:w-auto sm:justify-start"
              >
                Abbrechen
              </Button>
            </div>
            <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
              <Button
                type="button"
                onClick={handleSave}
                className="h-11 w-full px-6 sm:px-8 min-w-[140px] rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all sm:w-auto"
              >
                Einplanen
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
